import React from 'react';
import { Scale, FileText, Clock, AlertTriangle, CheckCircle, Calendar, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const CaliforniaCourtProceduresGuide = () => {
  const guideSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "California Court Procedures Guide 2025: Filing, Recording & Service",
    "description": "Complete guide to California court procedures including filing deadlines, service requirements, recording procedures, motion rules, and 2025 legislative updates.",
    "author": {
      "@type": "Organization",
      "name": "Legal Document Specialist"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Legal Document Specialist",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What are the new summary judgment deadlines in California for 2025?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Effective January 1, 2025, California summary judgment motions require notice and supporting documents filed 81 days before the hearing (increased from 75 days), oppositions filed 20 days before hearing (up from 14 days), and replies filed 11 days before hearing (up from 5 days). Additionally, no new evidence or material facts may be introduced in reply briefs under amended CCP § 437c(b)(4)."
        }
      },
      {
        "@type": "Question",
        "name": "How long do I have to serve a complaint in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In California, defendants must be served within 60 days after the complaint is filed. Proof of service must be filed within 60 days of filing the complaint. New defendants added by amended complaint must be served within 30 days. Failure to serve within these timeframes can result in case dismissal."
        }
      },
      {
        "@type": "Question",
        "name": "What is the deadline to file a notice of appeal in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The deadline to file a notice of appeal in California is strictly 60 days from entry of judgment for most civil appeals. This is a jurisdictional deadline that cannot be extended. The notice must be filed with the superior court (not the appellate court), and the record must be designated within 10 days after filing the notice of appeal."
        }
      },
      {
        "@type": "Question",
        "name": "When does electronic service of court notices begin in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Beginning July 1, 2025, California courts will electronically serve court-issued notices to attorneys in civil and family cases. Attorneys must keep current email addresses on file with the court. Electronic service provides two additional court days to respond compared to traditional mail or fax service."
        }
      },
      {
        "@type": "Question",
        "name": "What are the standard motion filing deadlines in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "For most motions (excluding summary judgment), notice and supporting papers must be filed at least 16 court days before the hearing. If service is by mail within California, add 5 calendar days. Oppositions are generally due 9 court days before the hearing, and replies are due 5 court days before the hearing. All papers must be served for delivery by close of next business day after filing."
        }
      },
      {
        "@type": "Question",
        "name": "What happens if a filing deadline falls on a weekend or holiday?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "If a deadline falls on a weekend or court holiday, it is moved to the previous court day (not the next day). Always consult the official California courts calendar for holidays affecting deadlines. For calculating deadlines, court days exclude weekends and holidays, while calendar days include all days."
        }
      }
    ]
  };

  React.useEffect(() => {
    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.textContent = JSON.stringify(guideSchema);
    document.head.appendChild(articleScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(guideSchema) || 
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="California Court Procedures Guide 2025: Filing, Recording & Service Deadlines"
        description="Complete guide to California court procedures: filing deadlines, service requirements, motion rules, appeal procedures, and 2025 legislative updates for legal professionals."
        keywords="California court procedures, court filing deadlines, service of process California, summary judgment deadlines, appeal deadlines, court rules 2025"
        canonical="/resources/california-court-procedures-guide"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'var(--bg-card)',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              marginBottom: '1.5rem',
              border: '1px solid var(--border-medium)'
            }}>
              <Scale size={16} color="var(--brand-primary)" />
              <span className="caption" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>
                Pillar Content Guide
              </span>
            </div>
            
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)',
              fontSize: 'clamp(2rem, 4vw, 3.5rem)'
            }}>
              California Court Procedures Guide 2025
            </h1>
            
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              Essential guide to California court filing deadlines, service requirements, recording procedures, and critical 2025 legislative updates for legal professionals.
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/court-filing" className="btn-primary">
                Court Filing Services
              </Link>
              <a href="/quote" className="btn-secondary">
                Request a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2025 Updates Alert */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '2rem',
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-start'
            }}>
              <AlertTriangle size={32} color="#F59E0B" style={{ flexShrink: 0 }} />
              <div>
                <h3 className="heading-3" style={{ marginBottom: '1rem', color: '#92400E' }}>
                  Critical 2025 Updates
                </h3>
                <p className="body-medium" style={{ color: '#78350F', lineHeight: '1.8', marginBottom: '1rem' }}>
                  <strong>Assembly Bill 2049</strong> enacted significant changes to summary judgment procedures effective January 1, 2025:
                </p>
                <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                  {[
                    'Motion deadline extended from 75 to 81 days before hearing',
                    'Opposition deadline extended from 14 to 20 days before hearing',
                    'Reply deadline extended from 5 to 11 days before hearing',
                    'NEW: No new evidence allowed in reply briefs',
                    'Electronic service of court notices begins July 1, 2025'
                  ].map((item, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      color: '#78350F'
                    }}>
                      <CheckCircle size={18} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filing Deadlines */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Filing Deadlines & Requirements
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Summary Judgment Motions */}
              <div style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2.5rem',
                borderRadius: '12px',
                border: '2px solid var(--brand-primary)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  <h3 className="heading-3" style={{ margin: 0 }}>
                    Summary Judgment Motions
                  </h3>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                    borderRadius: '50px',
                    fontSize: '0.7rem',
                    fontWeight: '600'
                  }}>
                    2025 UPDATED
                  </span>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem',
                  marginTop: '1.5rem'
                }}>
                  {[
                    {
                      title: 'Notice & Supporting Papers',
                      deadline: '81 days before hearing',
                      note: 'Increased from 75 days. Plus time for service (add 5 days if mail service within CA).'
                    },
                    {
                      title: 'Opposition Papers',
                      deadline: '20 days before hearing',
                      note: 'Increased from 14 days. Must serve for delivery by next business day close.'
                    },
                    {
                      title: 'Reply Papers',
                      deadline: '11 days before hearing',
                      note: 'Increased from 5 days. NO NEW EVIDENCE ALLOWED per CCP § 437c(b)(4).'
                    }
                  ].map((item, index) => (
                    <div key={index} style={{
                      backgroundColor: 'var(--bg-page)',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '1px solid var(--border-medium)'
                    }}>
                      <h4 className="heading-4" style={{ marginBottom: '0.5rem', color: 'var(--brand-primary)' }}>
                        {item.title}
                      </h4>
                      <p style={{
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        color: 'var(--text-primary)',
                        marginBottom: '0.75rem'
                      }}>
                        {item.deadline}
                      </p>
                      <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        {item.note}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Other Motions */}
              <div style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                  Standard Motion Deadlines
                </h3>
                
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.8' }}>
                  For most motions other than summary judgment, California follows these standard deadlines:
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem'
                }}>
                  {[
                    { title: 'Notice & Supporting Papers', deadline: '16 court days before hearing', note: 'Add 5 days if mail service in CA' },
                    { title: 'Opposition Papers', deadline: '9 court days before hearing', note: 'Serve for next-day delivery' },
                    { title: 'Reply Papers', deadline: '5 court days before hearing', note: 'Serve for next-day delivery' }
                  ].map((item, index) => (
                    <div key={index} style={{
                      backgroundColor: 'var(--bg-page)',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '1px solid var(--border-medium)'
                    }}>
                      <h4 className="heading-4" style={{ marginBottom: '0.5rem' }}>
                        {item.title}
                      </h4>
                      <p style={{
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: 'var(--brand-primary)',
                        marginBottom: '0.5rem'
                      }}>
                        {item.deadline}
                      </p>
                      <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {item.note}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service of Process Requirements */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Service of Process Requirements
            </h2>

            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.8' }}>
              Proper service of process is a constitutional requirement in California. Missing these critical deadlines can result in case dismissal or other sanctions.
            </p>

            <div style={{
              backgroundColor: 'var(--bg-page)',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid var(--border-medium)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--brand-primary)', color: 'white' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Document Type</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Service Deadline</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { doc: 'Complaint (Original)', deadline: '60 days after filing', notes: 'Proof of service also due within 60 days' },
                    { doc: 'Amended Complaint (New Defendants)', deadline: '30 days after filing', notes: 'Only applies to newly added defendants' },
                    { doc: 'Cross-Complaint (New Parties)', deadline: '30 days after filing', notes: 'Must serve new parties not already in case' },
                    { doc: 'Answer or Demurrer', deadline: '30 days from service', notes: 'Defendant\'s response deadline' },
                    { doc: 'Subpoena (Deposition)', deadline: '10 days minimum before', notes: 'Check specific subpoena type requirements' },
                    { doc: 'Notice of Entry of Judgment', deadline: 'After judgment entered', notes: 'Starts appeal period clock' }
                  ].map((item, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid var(--border-light)',
                      backgroundColor: index % 2 === 0 ? 'var(--bg-card)' : 'transparent'
                    }}>
                      <td style={{ padding: '1rem', fontWeight: '500' }}>{item.doc}</td>
                      <td style={{ padding: '1rem', color: 'var(--brand-primary)', fontWeight: '600' }}>{item.deadline}</td>
                      <td style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{item.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              backgroundColor: 'var(--bg-page)',
              borderRadius: '8px',
              border: '1px solid var(--border-medium)',
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-start'
            }}>
              <Clock size={24} color="var(--brand-primary)" style={{ flexShrink: 0 }} />
              <div>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  <strong>Critical:</strong> The 60-day deadline to serve the complaint is one of the most important deadlines in California civil procedure. Extensions are possible but require court approval and good cause. Always begin service attempts immediately after filing.
                </p>
              </div>
            </div>

            <div style={{ marginTop: '2rem' }}>
              <Link to="/process-serving" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--brand-primary)',
                textDecoration: 'none',
                fontWeight: '600'
              }}>
                Learn more about our Process Serving Services →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Appeal Procedures */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              California Appellate Procedures
            </h2>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '2px solid #DC2626',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <AlertTriangle size={28} color="#DC2626" style={{ flexShrink: 0 }} />
                <div>
                  <h3 className="heading-3" style={{ marginBottom: '1rem', color: '#991B1B' }}>
                    Jurisdictional Deadlines
                  </h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                    Appeal deadlines in California are <strong>strictly jurisdictional</strong> and cannot be extended. Missing these deadlines means the appellate court lacks jurisdiction to hear the appeal, resulting in automatic dismissal regardless of the merits of your case.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {[
                {
                  title: 'Notice of Appeal',
                  deadline: '60 days from entry of judgment',
                  requirements: [
                    'File with superior court (not appellate court)',
                    'Pay required filing fee',
                    'Include clear description of order/judgment appealed',
                    'Deadline is jurisdictional - no extensions possible'
                  ]
                },
                {
                  title: 'Designation of Record',
                  deadline: '10 days after filing notice of appeal',
                  requirements: [
                    'Specify documents for clerk\'s transcript',
                    'Order reporter\'s transcript if needed',
                    'Failure to designate can lead to dismissal',
                    'Extensions difficult to obtain'
                  ]
                },
                {
                  title: 'Appellant\'s Opening Brief',
                  deadline: '40 days after record filed (70 days without reporter\'s transcript)',
                  requirements: [
                    'Comprehensive brief with legal arguments',
                    'Proper citations to record',
                    'May stipulate to 60-day extension',
                    'Further extensions require court approval'
                  ]
                },
                {
                  title: 'Respondent\'s Brief',
                  deadline: '30 days after appellant\'s brief filed',
                  requirements: [
                    'Respond to appellant\'s arguments',
                    'May cross-appeal if needed',
                    'May stipulate to 60-day extension',
                    'Failure to file may result in default'
                  ]
                },
                {
                  title: 'Appellant\'s Reply Brief',
                  deadline: '20 days after respondent\'s brief filed',
                  requirements: [
                    'Address arguments in respondent\'s brief',
                    'Cannot raise new issues',
                    'May stipulate to extension',
                    'Optional but recommended'
                  ]
                }
              ].map((stage, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                      {stage.title}
                    </h3>
                    <p style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: 'var(--brand-primary)'
                    }}>
                      {stage.deadline}
                    </p>
                  </div>
                  
                  <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                    {stage.requirements.map((req, rIndex) => (
                      <li key={rIndex} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        marginBottom: '0.75rem'
                      }}>
                        <CheckCircle size={18} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recording & Filing Best Practices */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Best Practices for Court Filing & Recording
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {[
                {
                  icon: <Calendar size={24} />,
                  title: 'Calendar Everything',
                  description: 'Use a comprehensive tickler system to track all critical dates. Calendar both the deadline and several reminder dates before each deadline. Account for service time when calculating filing deadlines.'
                },
                {
                  icon: <Clock size={24} />,
                  title: 'File Early',
                  description: 'Never wait until the last day to file. Technical issues, court closures, or service problems can cause delays. Aim to file at least 2-3 days before deadlines when possible.'
                },
                {
                  icon: <FileText size={24} />,
                  title: 'Check Local Rules',
                  description: 'Always verify local court rules for specific requirements. Each of California\'s 58 superior courts may have unique procedures, formatting requirements, and local deadline variations.'
                },
                {
                  icon: <BookOpen size={24} />,
                  title: 'Stay Updated',
                  description: 'Subscribe to court updates and rule changes. The 2025 summary judgment changes demonstrate that procedures evolve. Follow court websites and legal news sources for updates.'
                },
                {
                  icon: <AlertTriangle size={24} />,
                  title: 'Account for Holidays',
                  description: 'Consult the official California courts calendar for holidays. If a deadline falls on a weekend or holiday, it moves to the previous court day. Plan accordingly for year-end and summer holidays.'
                },
                {
                  icon: <Scale size={24} />,
                  title: 'Use Professional Services',
                  description: 'For critical filings and service, use professional court filing and process serving services. They understand requirements, maintain court relationships, and reduce the risk of procedural errors.'
                }
              ].map((practice, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    color: 'white'
                  }}>
                    {practice.icon}
                  </div>
                  
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {practice.title}
                  </h3>
                  
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {practice.description}
                  </p>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '3rem', textAlign: 'center' }}>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                Need professional court filing or county recording services?
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/court-filing" style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--brand-primary)',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}>
                  Court Filing Services
                </Link>
                <Link to="/county-recording" style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'transparent',
                  color: 'var(--brand-primary)',
                  border: '2px solid var(--brand-primary)',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}>
                  County Recording Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'What are the new summary judgment deadlines in California for 2025?',
                answer: 'Effective January 1, 2025, California summary judgment motions require notice and supporting documents filed 81 days before the hearing (increased from 75 days), oppositions filed 20 days before hearing (up from 14 days), and replies filed 11 days before hearing (up from 5 days). Additionally, no new evidence or material facts may be introduced in reply briefs under amended CCP § 437c(b)(4).'
              },
              {
                question: 'How long do I have to serve a complaint in California?',
                answer: 'In California, defendants must be served within 60 days after the complaint is filed. Proof of service must be filed within 60 days of filing the complaint. New defendants added by amended complaint must be served within 30 days. Failure to serve within these timeframes can result in case dismissal.'
              },
              {
                question: 'What is the deadline to file a notice of appeal in California?',
                answer: 'The deadline to file a notice of appeal in California is strictly 60 days from entry of judgment for most civil appeals. This is a jurisdictional deadline that cannot be extended. The notice must be filed with the superior court (not the appellate court), and the record must be designated within 10 days after filing the notice of appeal.'
              },
              {
                question: 'When does electronic service of court notices begin in California?',
                answer: 'Beginning July 1, 2025, California courts will electronically serve court-issued notices to attorneys in civil and family cases. Attorneys must keep current email addresses on file with the court. Electronic service provides two additional court days to respond compared to traditional mail or fax service.'
              },
              {
                question: 'What are the standard motion filing deadlines in California?',
                answer: 'For most motions (excluding summary judgment), notice and supporting papers must be filed at least 16 court days before the hearing. If service is by mail within California, add 5 calendar days. Oppositions are generally due 9 court days before the hearing, and replies are due 5 court days before the hearing. All papers must be served for delivery by close of next business day after filing.'
              },
              {
                question: 'What happens if a filing deadline falls on a weekend or holiday?',
                answer: 'If a deadline falls on a weekend or court holiday, it is moved to the previous court day (not the next day). Always consult the official California courts calendar for holidays affecting deadlines. For calculating deadlines, court days exclude weekends and holidays, while calendar days include all days.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Need Expert Court Filing Services?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Professional e-filing and traditional court filing throughout all 58 California counties
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/court-filing"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              View Court Filing Services
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaliforniaCourtProceduresGuide;
